// ✅ Visitor Management Sidebar
// Matches SetupSidebar look & feel
// Created on 2025-11-19

import React, { useEffect, useRef, useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MdHomeFilled,
  MdDashboard,
  MdPersonAdd,
  MdCheckCircle,
  MdQrCodeScanner,
  MdExitToApp,
  MdListAlt,
  MdChevronLeft,
  MdMenu,
} from "react-icons/md";

import { AuthContext } from "../../context/AuthContext";
import "../../components/sidebar.css"; // reuse the same CSS

const LS_KEY = "visitor_sidebar_collapsed";

export default function VisitorSidebar({ mobileOpen, setMobileOpen }) {
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);

  const [collapsed, setCollapsed] = useState(
    localStorage.getItem(LS_KEY) === "true"
  );
  const [hoverExpand, setHoverExpand] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY, collapsed);
  }, [collapsed]);

  const expanded =
    !collapsed || hoverExpand || (isMobile && mobileOpen === true);

  // -----------------------------------------
  // Visitor Menu Configuration
  // -----------------------------------------
  const menu = [
    {
      key: "back",
      label: "Back to Dashboard",
      icon: <MdHomeFilled size={22} />,
      to: "/dashboard",
      isPrimary: true,
    },
    {
      key: "visitor-dashboard",
      label: "Dashboard",
      to: "/visitor/dashboard",
      icon: <MdDashboard size={22} />,
      permission: "VISITOR_VIEW:view",
    },
    {
      key: "create-visit",
      label: "Create Visit",
      to: "/visitor/create",
      icon: <MdPersonAdd size={22} />,
      permission: "VISITOR_CREATE:view",
    },
    {
      key: "approvals",
      label: "Approvals",
      to: "/visitor/approvals",
      icon: <MdCheckCircle size={22} />,
      permission: "VISITOR_APPROVE:view",
    },
    {
      key: "checkin",
      label: "Visitor Check-In",
      to: "/visitor/checkin",
      icon: <MdQrCodeScanner size={22} />,
      permission: "VISITOR_CHECKIN:view",
    },
    {
      key: "checkout",
      label: "Visitor Check-Out",
      to: "/visitor/checkout",
      icon: <MdExitToApp size={22} />,
      permission: "VISITOR_CHECKOUT:view",
    },
    {
      key: "reports",
      label: "Reports",
      to: "/visitor/reports",
      icon: <MdListAlt size={22} />,
      permission: "VISITOR_REPORT_VIEW:view",
    },
  ];

  const handleCollapseToggle = () => {
    if (isMobile) return setMobileOpen(false);
    setCollapsed((c) => !c);
  };

  const handleHomeClick = () => {
    navigate("/dashboard");
    if (isMobile) setMobileOpen(false);
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          className="tf-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        ref={sidebarRef}
        className={`tf-sidebar ${expanded ? "expanded" : "collapsed"} ${
          isMobile && mobileOpen ? "open" : ""
        }`}
        onMouseLeave={() => setHoverExpand(false)}
      >
        <div
          className="tf-sidebar-hover-edge"
          onMouseEnter={() => collapsed && !isMobile && setHoverExpand(true)}
        />

        {/* Header */}
        <div className="tf-sidebar-header">
          <div className="tf-logo">
            <span className="tf-logo-icon">VM</span>
            {expanded && <span className="tf-logo-text">Visitor</span>}
          </div>

          <button
            className="tf-icon-btn"
            onClick={handleCollapseToggle}
            onMouseEnter={() =>
              collapsed && !isMobile && setHoverExpand(true)
            }
          >
            {isMobile ? <MdMenu /> : <MdChevronLeft />}
          </button>
        </div>

        <nav className="tf-nav">
          {menu.map((m) => {
            if (m.permission) {
              const [func, action] = m.permission.split(":");
              if (!hasPermission(func, action)) return null;
            }

            if (m.isPrimary && !expanded) {
              return (
                <button
                  key={m.key}
                  className="tf-home-icon"
                  onClick={handleHomeClick}
                  title={m.label}
                >
                  {m.icon}
                </button>
              );
            }

            if (m.isPrimary && expanded) {
              return (
                <button
                  key={m.key}
                  className="tf-back-primary"
                  onClick={handleHomeClick}
                >
                  {m.icon}
                  <span className="tf-label">{m.label}</span>
                </button>
              );
            }

            return (
              <NavLink
                key={m.key}
                to={m.to}
                className={({ isActive }) =>
                  `tf-nav-item ${isActive ? "active" : ""}`
                }
                title={!expanded ? m.label : ""}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="tf-icon">{m.icon}</span>
                {expanded && <span className="tf-label">{m.label}</span>}
                <span className="tf-active-indicator" />
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
